/**
 * schema.org JSON-LD builders. Sab data src/config/business.ts se aata hai,
 * khaali field chhod diye jaate hain (Google ko adhoora data se khaali behtar).
 * Use: useJsonLd("org", organizationSchema()) — dekho common/hooks/useJsonLd.ts
 */
import { BUSINESS } from "../../config/business";

const SITE = BUSINESS.url;

/** undefined/khaali keys hata do, warna JSON-LD me "null" chala jaata hai. */
const clean = <T extends Record<string, any>>(o: T): T => {
  Object.keys(o).forEach((k) => {
    const v = (o as any)[k];
    if (v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)) delete (o as any)[k];
  });
  return o;
};

const postalAddress = () =>
  clean({
    "@type": "PostalAddress",
    streetAddress: BUSINESS.address.street,
    addressLocality: BUSINESS.address.locality,
    addressRegion: BUSINESS.address.region,
    postalCode: BUSINESS.address.postalCode,
    addressCountry: BUSINESS.address.country,
  });

/** Home par ek baar — company identity (knowledge panel ke liye). */
export const organizationSchema = () =>
  clean({
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE}/#organization`,
    name: BUSINESS.legalName,
    alternateName: BUSINESS.name,
    url: SITE,
    logo: BUSINESS.logo,
    description: BUSINESS.description,
    telephone: BUSINESS.telephone,
    email: BUSINESS.email,
    foundingDate: BUSINESS.foundingDate,
    vatID: BUSINESS.gstin,
    address: postalAddress(),
    sameAs: BUSINESS.sameAs,
  });

/** "building material supplier rudrapur" type local search ke liye. */
export const localBusinessSchema = () =>
  clean({
    "@context": "https://schema.org",
    "@type": "HardwareStore",
    "@id": `${SITE}/#store`,
    name: BUSINESS.legalName,
    image: BUSINESS.logo,
    url: SITE,
    description: BUSINESS.description,
    telephone: BUSINESS.telephone,
    priceRange: BUSINESS.priceRange,
    currenciesAccepted: BUSINESS.currenciesAccepted,
    paymentAccepted: BUSINESS.paymentAccepted,
    address: postalAddress(),
    geo: BUSINESS.geo
      ? { "@type": "GeoCoordinates", latitude: BUSINESS.geo.lat, longitude: BUSINESS.geo.lng }
      : undefined,
    openingHours: BUSINESS.openingHours,
    areaServed: BUSINESS.areaServed.map((a) => ({ "@type": "City", name: a })),
    sameAs: BUSINESS.sameAs,
    parentOrganization: { "@id": `${SITE}/#organization` },
  });

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE}/#website`,
  url: SITE,
  name: BUSINESS.name,
  inLanguage: "en-IN",
  publisher: { "@id": `${SITE}/#organization` },
});

export type Crumb = { name: string; path: string };

/** Breadcrumb — Google search result me "Home > TMT Bar > ..." dikhata hai. */
export const breadcrumbSchema = (crumbs: Crumb[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: `${SITE}${c.path}`,
  })),
});

/** Category listing ke liye — top N product ka ItemList. */
export const itemListSchema = (name: string, items: { name: string; path: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name,
  numberOfItems: items.length,
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    url: `${SITE}${it.path}`,
  })),
});

export const faqSchema = (qa: { q: string; a: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: qa.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});
